import React from 'react'
import { ToothNumberingSystem } from '@dataTypes/chartingPreferences.type'
import {
  PerioChartSectionData,
  PerioSection,
} from '@dataTypes/chartingFeature.types'
import {
  getProbeSiteLabels,
  getToothIndex,
  toDisplayOrder,
} from '@utils/perio.utils'
import ToothNumberLabel from '@components/pages/Charting/Dental/ToothNumberLabel.component'
import { PERIO_METRIC_LABELS } from './perio-chart-table.utils'

const FURCATION_PATH =
  'M7.16 2.1C7.25 1.95 7.38 1.82 7.53 1.73C7.68 1.64 7.86 1.6 8.03 1.6C8.21 1.6 8.38 1.64 8.53 1.73C8.68 1.82 8.81 1.95 8.9 2.1L14.65 12.07C14.74 12.22 14.79 12.39 14.79 12.57C14.79 12.74 14.74 12.92 14.65 13.07C14.57 13.22 14.44 13.35 14.29 13.43C14.14 13.52 13.96 13.57 13.79 13.57H2.28C2.1 13.57 1.93 13.52 1.78 13.43C1.62 13.35 1.5 13.22 1.41 13.07C1.32 12.92 1.28 12.74 1.28 12.57C1.28 12.39 1.32 12.22 1.41 12.07L7.16 2.1Z'
const MARKER_PATH =
  'M7.53 1.55C7.67 1.43 7.85 1.37 8.03 1.37C8.21 1.37 8.39 1.43 8.53 1.55C9.49 2.34 10.37 3.23 11.15 4.2C12.25 5.56 13.36 7.4 13.36 9.33C13.36 10.74 12.8 12.1 11.8 13.1C10.8 14.1 9.45 14.66 8.03 14.66C6.62 14.66 5.26 14.1 4.26 13.1C3.26 12.1 2.7 10.74 2.7 9.33C2.7 7.4 3.82 5.56 4.91 4.2C5.69 3.23 6.57 2.34 7.53 1.55Z'

const ICON_LABELS = ['Bleeding', 'Calculus', 'Supp', 'Furcation'] as const
const TRIPLE_LABELS = ['PD', 'GM', 'CAL'] as const

type IconLabel = (typeof ICON_LABELS)[number]
type TripleLabel = (typeof TRIPLE_LABELS)[number]

const isIconLabel = (label: string): label is IconLabel =>
  (ICON_LABELS as readonly string[]).includes(label)

const isTripleLabel = (label: string): label is TripleLabel =>
  (TRIPLE_LABELS as readonly string[]).includes(label)

const iconFill = (label: IconLabel): string => {
  switch (label) {
    case 'Bleeding':
      return '#fe746b'
    case 'Calculus':
      return '#e3b016'
    case 'Supp':
      return '#65acf5'
    case 'Furcation':
      return '#e46910'
  }
}

interface PerioMetricRowProps {
  section: PerioSection
  label: keyof PerioChartSectionData
  teeth: readonly number[]
  values: readonly string[] | undefined
  cursorTooth: number | null
  cursorRef: React.RefObject<HTMLTableCellElement>
}

const PerioIconCell = ({
  label,
  tooth,
  value,
}: {
  label: IconLabel
  tooth: number
  value: string
}): React.ReactElement => {
  const activeSet = new Set(value.split(' '))
  const fill = iconFill(label)
  const path = label === 'Furcation' ? FURCATION_PATH : MARKER_PATH
  return (
    <div className="triple-icon-cell d-flex justify-content-center gap-1">
      {getProbeSiteLabels(tooth).map((surface, index) => (
        <svg
          key={index}
          width="17"
          height="16"
          viewBox="0 0 17 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            opacity: activeSet.has(surface)
              ? 1
              : 'var(--perio-marker-inactive-opacity, 0.15)',
            transition: 'opacity 0.2s ease',
          }}
        >
          <path fillRule="evenodd" clipRule="evenodd" d={path} fill={fill} />
        </svg>
      ))}
    </div>
  )
}

const PerioMetricValue = ({
  label,
  tooth,
  values,
}: {
  label: keyof PerioChartSectionData
  tooth: number
  values: readonly string[] | undefined
}): React.ReactElement | null => {
  const index = getToothIndex(tooth)
  if (isIconLabel(label)) {
    return (
      <PerioIconCell
        label={label}
        tooth={tooth}
        value={values?.[index] ?? ''}
      />
    )
  }
  if (isTripleLabel(label)) {
    const raw = values?.[index] ?? '0 0 0'
    const value = toDisplayOrder(tooth, raw.split(' ')).join(' ')
    return <div className="triple-value-cell dbm-text-spacing">{value}</div>
  }
  if (label === 'Mobility') {
    const value = (values?.[index] ?? '0').split(' ')[0]
    return <div className="single-value-cell">{value}</div>
  }
  return null
}

const PerioMetricRow = React.memo(function PerioMetricRow({
  section,
  label,
  teeth,
  values,
  cursorTooth,
  cursorRef,
}: PerioMetricRowProps): React.ReactElement {
  return (
    <tr>
      <td className="label-cell">{label}</td>
      {teeth.map((tooth) => {
        const selected = cursorTooth === tooth
        return (
          <td
            key={tooth}
            ref={selected ? cursorRef : undefined}
            className={selected ? 'perio-voice-cursor-cell' : undefined}
            aria-current={selected ? 'true' : undefined}
            aria-label={
              selected
                ? `Voice cursor: Universal tooth ${tooth} ${section} ${label}`
                : undefined
            }
          >
            <PerioMetricValue label={label} tooth={tooth} values={values} />
          </td>
        )
      })}
    </tr>
  )
})

export interface PerioSectionTableProps {
  section: PerioSection
  teeth: readonly number[]
  sectionData: PerioChartSectionData | undefined
  showDBMRow: boolean
  showToothNumbers: boolean
  labelAtBottom?: boolean
  numberingSystem: ToothNumberingSystem
  highlightTooth: number | null
  cursorTooth: number | null
  cursorMetric: keyof PerioChartSectionData | null
  cursorRef: React.RefObject<HTMLTableCellElement>
}

const PerioSectionTable = ({
  section,
  teeth,
  sectionData,
  showDBMRow,
  showToothNumbers,
  labelAtBottom = false,
  numberingSystem,
  highlightTooth,
  cursorTooth,
  cursorMetric,
  cursorRef,
}: PerioSectionTableProps): React.ReactElement => {
  const sectionTitle = section === 'facial' ? 'Facial' : 'Lingual'
  return (
    <table className="perio-chart-table">
      <thead>
        {showToothNumbers && (
          <tr>
            <th></th>
            {teeth.map((tooth) => (
              <th
                key={tooth}
                className={
                  highlightTooth === tooth
                    ? 'perio-voice-cursor-tooth'
                    : undefined
                }
              >
                <ToothNumberLabel
                  universalToothNumber={tooth}
                  numberingSystem={numberingSystem}
                />
              </th>
            ))}
          </tr>
        )}
        {!labelAtBottom && (
          <tr>
            <td colSpan={17} className="section-label">
              {sectionTitle}
            </td>
          </tr>
        )}
        {showDBMRow && (
          <tr className="dbm-row">
            <td></td>
            {teeth.map((tooth) => (
              <td key={tooth} className="dbm-text-spacing">
                {getProbeSiteLabels(tooth).join(' ')}
              </td>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {PERIO_METRIC_LABELS.map((label) => (
          <PerioMetricRow
            key={label}
            section={section}
            label={label}
            teeth={teeth}
            values={sectionData?.[label]}
            cursorTooth={cursorMetric === label ? cursorTooth : null}
            cursorRef={cursorRef}
          />
        ))}
        {labelAtBottom && (
          <tr>
            <td colSpan={17} className="section-label">
              {sectionTitle}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

export default React.memo(PerioSectionTable)
